import { defineStore } from "pinia";
import { useTenantStore } from "./tenantStore";

export const useActionCableDebugStore = defineStore("actionCableDebug", {
  state: () => ({
    enabled: true, // ActionCable enabled by default
    connections: new Map(), // Track all active connections
    messageHistory: [], // Track last 50 messages
    maxHistorySize: 50,
    allStores: new Set(), // Track all stores for global control
  }),

  getters: {
    isEnabled: (state) => state.enabled,

    connectionCount: (state) => state.connections.size,

    activeConnections: (state) => {
      return Array.from(state.connections.entries()).map(([name, data]) => ({
        name,
        connected: data.connected,
        channel: data.channel,
        subscriptionTime: data.subscriptionTime,
      }));
    },

    recentMessages: (state) => state.messageHistory.slice(-10),

    isDebugMode: () => {
      const tenant = useTenantStore();
      return tenant.isDebugMode;
    },
  },

  actions: {
    toggle() {
      this.enabled = !this.enabled;
      console.log(`🔌 ActionCable ${this.enabled ? "ENABLED" : "DISABLED"}`);

      if (this.enabled) {
        // Re-subscribe all registered stores
        this.subscribeAll();
      } else {
        // Disconnect all active connections
        this.disconnectAll();
      }
    },

    enable() {
      this.enabled = true;
      console.log(`🔌 ActionCable ENABLED`);
      this.subscribeAll();
    },

    disable() {
      this.enabled = false;
      console.log(`🔌 ActionCable DISABLED`);
      this.disconnectAll();
    },

    // Register a store for global control
    registerStore(store) {
      this.allStores.add(store);
      console.log(`🔧 Registered store: ${store.name || "Unknown"}`);
    },

    // Subscribe all registered stores
    subscribeAll() {
      console.log(
        `🔌 Subscribing all ${this.allStores.size} registered stores...`,
      );
      this.allStores.forEach((store) => {
        if (
          store.subscribeToActionCable &&
          typeof store.subscribeToActionCable === "function"
        ) {
          try {
            store.subscribeToActionCable();
          } catch (error) {
            console.error(`Error subscribing store ${store.name}:`, error);
          }
        }
      });
    },

    disconnectAll() {
      console.log(
        `🔌 Disconnecting all ${this.connections.size} connections...`,
      );

      // First try to disconnect through registered stores
      this.allStores.forEach((store) => {
        if (
          store.unsubscribeFromActionCable &&
          typeof store.unsubscribeFromActionCable === "function"
        ) {
          try {
            store.unsubscribeFromActionCable();
          } catch (error) {
            console.error(`Error unsubscribing store ${store.name}:`, error);
          }
        }
      });

      // Then disconnect any remaining connections
      this.connections.forEach((connection, name) => {
        if (connection.subscription) {
          try {
            connection.subscription.unsubscribe();
            console.log(`🔌 Force disconnected ${name}`);
          } catch (error) {
            console.error(`Error force disconnecting ${name}:`, error);
          }
        }
      });

      this.connections.clear();
    },

    registerConnection(name, channel, subscription) {
      console.log(`🔧 Registering connection: ${name} -> ${channel}`);
      this.connections.set(name, {
        channel,
        subscription,
        connected: false,
        subscriptionTime: Date.now(),
      });
    },

    updateConnectionStatus(name, connected) {
      const connection = this.connections.get(name);
      if (connection) {
        connection.connected = connected;
        console.log(
          `🔧 Connection ${name}: ${connected ? "CONNECTED" : "DISCONNECTED"}`,
        );
      }
    },

    logMessage(channel, message) {
      const logEntry = {
        timestamp: Date.now(),
        channel,
        message: JSON.stringify(message),
        id: Math.random().toString(36).substr(2, 9),
      };

      this.messageHistory.push(logEntry);

      // Keep only recent messages
      if (this.messageHistory.length > this.maxHistorySize) {
        this.messageHistory = this.messageHistory.slice(-this.maxHistorySize);
      }
    },

    clearHistory() {
      this.messageHistory = [];
    },
  },
});
